// Full-chart snapshot. klinecharts' getConvertPictureUrl only captures the
// <canvas> layers (candles, indicators, drawings, volume). The status line, the
// MA/Vol legends and the sub-pane (RSI/MACD) legends are HTML positioned over the
// canvas, so a canvas-only grab misses them. This captures the WHOLE chart area:
// every <canvas> is swapped for an <img> of its pixels, computed styles are
// inlined, and the result is rasterised via an SVG <foreignObject> — so the text
// overlays land on top, exactly as seen. Dependency-free.

// Recursively copy computed styles onto the clone so the foreignObject render matches the
// live DOM (foreignObject ignores external stylesheets).
function inlineStyles(src: Element, dest: Element): void {
  const cs = getComputedStyle(src)
  let css = ''
  for (let i = 0; i < cs.length; i++) {
    const prop = cs[i]
    css += `${prop}:${cs.getPropertyValue(prop)};`
  }
  dest.setAttribute('style', css)
  const sk = src.children
  const dk = dest.children
  for (let i = 0; i < sk.length; i++) if (dk[i]) inlineStyles(sk[i], dk[i])
}

// The AlgoBuilder brand mark as a standalone SVG string (mirrors AlgoBuilderMark),
// theme-aware overlay so it reads on either background. Used for the screenshot watermark.
function markSvg(dark: boolean): string {
  const from = dark ? 'white' : '#0C111D'
  const to = dark ? '#0C111D' : 'white'
  const D1 = 'M11.6121 0.820068L22.1729 29.5739L10.8749 23.86L0 29.7583L11.6121 0.820068Z'
  const D2 =
    'M27.3898 0.922175L14.1338 1.05161L24.4995 29.5538L29.4995 29.0495C33.0278 28.9303 35.7793 26.0791 35.6423 22.0244C35.5196 18.393 33.1236 15.4549 30.0901 14.9666C32.6611 13.8797 34.4308 11.2072 34.3274 8.1452C34.2007 4.39666 31.5208 1.09861 28.0001 0.921521L27.9994 0.916223L27.8967 0.917226C27.7654 0.912926 27.633 0.912962 27.4995 0.917471C27.4629 0.918708 27.4263 0.920277 27.3898 0.922175Z'
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 30" width="36" height="30" fill="none">` +
    `<path d="${D1}" fill="url(#mk0)"/><path d="${D1}" fill="url(#mk1)"/>` +
    `<path fill-rule="evenodd" clip-rule="evenodd" d="${D2}" fill="url(#mk2)"/>` +
    `<path fill-rule="evenodd" clip-rule="evenodd" d="${D2}" fill="url(#mk3)"/>` +
    `<defs>` +
    `<linearGradient id="mk0" x1="-42.3964" y1="18.3758" x2="-17.4316" y2="-20.2939" gradientUnits="userSpaceOnUse"><stop stop-color="#0C111D"/><stop offset="0.325461" stop-color="#0E224B"/><stop offset="1" stop-color="#155EEF"/></linearGradient>` +
    `<linearGradient id="mk1" x1="2.07695" y1="15.4467" x2="51.6086" y2="17.2146" gradientUnits="userSpaceOnUse"><stop stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient>` +
    `<linearGradient id="mk2" x1="-25.0016" y1="19.8772" x2="-0.800669" y2="-18.0789" gradientUnits="userSpaceOnUse"><stop stop-color="#0C111D"/><stop offset="0.325461" stop-color="#0E224B"/><stop offset="1" stop-color="#155EEF"/></linearGradient>` +
    `<linearGradient id="mk3" x1="16.572" y1="15.5525" x2="63.0433" y2="15.5433" gradientUnits="userSpaceOnUse"><stop stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient>` +
    `</defs></svg>`
  )
}

/** PNG data URL of the whole chart wrapper (canvas layers + HTML overlays). */
export async function captureWorkbench(wrapEl: HTMLElement, bg: string): Promise<string> {
  const rect = wrapEl.getBoundingClientRect()
  const W = Math.max(1, Math.round(rect.width))
  const H = Math.max(1, Math.round(rect.height))
  const dpr = window.devicePixelRatio || 1

  const clone = wrapEl.cloneNode(true) as HTMLElement
  clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml')
  // Drop transient UI that isn't chart content (e.g. the floating style toolbar).
  clone.querySelectorAll('[data-snapshot-exclude]').forEach((n) => n.remove())

  // Swap each live <canvas> for an <img> of its current pixels (canvases don't serialise).
  const srcCanvases = wrapEl.querySelectorAll('canvas')
  const cloneCanvases = clone.querySelectorAll('canvas')
  srcCanvases.forEach((c, i) => {
    const dst = cloneCanvases[i]
    if (!dst) return
    let url: string
    try {
      url = c.toDataURL('image/png')
    } catch {
      return
    }
    const img = document.createElement('img')
    img.setAttribute('src', url)
    img.setAttribute(
      'style',
      `${dst.getAttribute('style') || ''};width:${c.clientWidth}px;height:${c.clientHeight}px;`,
    )
    dst.replaceWith(img)
  })

  inlineStyles(wrapEl, clone)
  clone.style.background = bg
  clone.style.margin = '0'

  const xml = new XMLSerializer().serializeToString(clone)
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">` +
    `<foreignObject x="0" y="0" width="${W}" height="${H}">${xml}</foreignObject></svg>`

  const overlayImg = new Image()
  await new Promise<void>((resolve, reject) => {
    overlayImg.onload = () => resolve()
    overlayImg.onerror = reject
    overlayImg.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  })

  const out = document.createElement('canvas')
  out.width = Math.round(W * dpr)
  out.height = Math.round(H * dpr)
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.scale(dpr, dpr)
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, W, H)
  ctx.drawImage(overlayImg, 0, 0, W, H)

  // AlgoBuilder watermark — screenshots only (top-right, subtle): the brand mark + text.
  const dark = bg.toLowerCase() === '#1a1a1a'
  const text = 'AlgoBuilder'
  const fontPx = 11
  ctx.font = `600 ${fontPx}px Inter, system-ui, -apple-system, sans-serif`
  const textW = ctx.measureText(text).width
  const markH = 13
  const markW = (markH * 36) / 30
  const gap = 5
  const padR = 12
  const padT = 10
  const centerY = padT + markH / 2
  const textRight = W - padR
  const logoX = textRight - textW - gap - markW

  const logo = new Image()
  await new Promise<void>((resolve) => {
    logo.onload = () => resolve()
    logo.onerror = () => resolve() // still draw the text if the mark fails
    logo.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markSvg(dark))
  })
  try {
    ctx.drawImage(logo, logoX, centerY - markH / 2, markW, markH)
  } catch {
    /* skip mark */
  }

  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = dark ? 'rgba(228,228,228,0.6)' : 'rgba(26,26,26,0.5)'
  ctx.fillText(text, textRight, centerY + 0.5)

  return out.toDataURL('image/png')
}
